import React, { useEffect, useRef, useState } from 'react'
import { Grid, Typography } from '@mui/material'
import { makeStyles } from '@mui/styles'
import classNames from 'classnames'
import { Moment } from 'moment'

import moment from '../../../momentLocale'
import PuiPopper from '../../PuiPopper'

const useStyles = makeStyles(
  theme => ({
    root: {
      top: 0,
      left: 0,
      position: 'absolute',
    },
    overlay: {
      zIndex: theme.zIndex.modal + 1,
      cursor: 'pointer',
      position: 'absolute',
      top: 16,
      height: 32,
    },
    inlineOverlay: {
      top: 8,
    },
    monthOverlay: {
      left: 36,
      width: 100,
    },
    yearOverlay: {
      right: 120,
      width: 70,
    },
    inlineMonthOverlay: {
      width: 110,
      left: 40,
      top: 15,
    },
    inlineYearOverlay: {
      right: 95,
      top: 15,
    },
    text: {
      justifyContent: 'flex-start',
      lineHeight: '3.2rem',
      paddingLeft: theme.spacing(1),
      color: theme.colors.secondaryText,
      fontSize: '1.6rem',
      fontWeight: 400,
      cursor: 'pointer',
      flexShrink: 0,
      '&&&&:hover': {
        backgroundColor: theme.colors.patientAvatarBackground,
      },
    },
    activeText: {
      backgroundColor: theme.colors.patientAvatarBackground,
    },
    dateContainer: {
      paddingLeft: theme.spacing(1),
      paddingRight: theme.spacing(1),
      overflowY: 'auto',
      ...theme.constants.scrollbar,
    },
    monthsContainer: {
      borderRight: theme.constants.pickerBorder,
    },
    yearsContainer: {
      borderLeft: theme.constants.pickerBorder,
    },
    monthPaper: {
      width: 125,
    },
    yearPaper: {
      width: 90,
    },
    paper: {
      height: 290,
      paddingTop: theme.spacing(0.5),
      display: 'flex',
      userSelect: 'none',
      boxShadow: theme.constants.blockShadow,
    },
  }),
  { name: 'CalendarToolbarComponent' },
)

const dateStart = moment().year(1900)
const dateEnd = moment().add(20, 'y')

const years: number[] = []

while (dateEnd >= dateStart) {
  years.push(Number(dateStart.format('YYYY')))
  dateStart.add(1, 'year')
}

const months = moment.months()

export interface CalendarToolbarComponentProps<TDate = Moment> {
  readonly onChange: (date: TDate | null) => void
  readonly parsedValue: TDate
  readonly showTopPickers?: boolean
  readonly variant?: 'static' | 'inline'
}

const CalendarToolbarComponent = ({
  showTopPickers = false,
  onChange,
  variant,
  parsedValue: dateProp,
}: CalendarToolbarComponentProps) => {
  const classes = useStyles()

  const [monthPopperOpen, setMonthPopperOpen] = useState(false)
  const [yearPopperOpen, setYearPopperOpen] = useState(false)

  const monthOverlayRef = useRef<HTMLDivElement>(null)
  const yearOverlayRef = useRef<HTMLDivElement>(null)
  const monthsContainerRef = useRef<HTMLDivElement>(null)
  const yearsContainerRef = useRef<HTMLDivElement>(null)

  const date = moment(dateProp || undefined)

  const month = date.format('MMMM')
  const year = Number(date.format('YYYY'))

  useEffect(() => {
    if (yearsContainerRef.current) {
      yearsContainerRef.current.scrollTop = (years.indexOf(year) - 5) * 32
    }
  }, [yearPopperOpen])

  useEffect(() => {
    if (monthsContainerRef.current) {
      monthsContainerRef.current.scrollTop = (months.indexOf(month) - 5) * 32
    }
  }, [monthPopperOpen])

  const saveChanges = (newMonth: string, newYear: number) => {
    const newDate = moment(date)

    if (newMonth) {
      newDate.month(newMonth)
    }

    if (newYear) {
      newDate.year(newYear)
    }

    onChange(newDate)
  }

  const onMonthOverlayClick = () => {
    setMonthPopperOpen(!monthPopperOpen)
  }

  const onYearOverlayClick = () => {
    setYearPopperOpen(!yearPopperOpen)
  }

  const onMonthPopperClose = () => {
    setMonthPopperOpen(false)
  }

  const onYearPopperClose = () => {
    setYearPopperOpen(false)
  }

  const onMonthChange = (newMonth: string) => {
    saveChanges(newMonth, year)
  }

  const onYearChange = (newYear: number) => {
    saveChanges(month, newYear)
  }

  const isInline = variant !== 'static'

  return (
    <Grid container item className={classes.root} wrap="nowrap">
      {showTopPickers && (
        <>
          <div
            className={classNames(classes.overlay, classes.monthOverlay, {
              [classes.inlineMonthOverlay]: isInline,
              [classes.inlineOverlay]: isInline,
            })}
            ref={monthOverlayRef}
            onClick={onMonthOverlayClick}
          >
            <PuiPopper
              hideCloseButton
              anchorEl={monthOverlayRef.current}
              classes={{
                paper: classNames(classes.paper, classes.monthPaper),
              }}
              eventName="onClick"
              modifiers={[
                {
                  name: 'flip',
                  enabled: false,
                },
              ]}
              open={monthPopperOpen}
              placement="bottom"
              onClose={onMonthPopperClose}
            >
              <Grid
                container
                className={classNames(
                  classes.dateContainer,
                  classes.monthsContainer,
                )}
                direction="column"
                ref={monthsContainerRef}
                wrap="nowrap"
              >
                {months.map(m => (
                  <Typography
                    className={classNames(classes.text, {
                      [classes.activeText]: month === m,
                    })}
                    key={m}
                    onClick={() => onMonthChange(m)}
                  >
                    {m}
                  </Typography>
                ))}
              </Grid>
            </PuiPopper>
          </div>
          <div
            className={classNames(classes.overlay, classes.yearOverlay, {
              [classes.inlineYearOverlay]: isInline,
              [classes.inlineOverlay]: isInline,
            })}
            ref={yearOverlayRef}
            onClick={onYearOverlayClick}
          >
            <PuiPopper
              hideCloseButton
              anchorEl={yearOverlayRef.current}
              classes={{
                paper: classNames(classes.paper, classes.yearPaper),
              }}
              eventName="onClick"
              modifiers={[
                {
                  name: 'flip',
                  enabled: false,
                },
              ]}
              open={yearPopperOpen}
              placement="bottom"
              onClose={onYearPopperClose}
            >
              <Grid
                container
                className={classNames(
                  classes.dateContainer,
                  classes.yearsContainer,
                )}
                direction="column"
                ref={yearsContainerRef}
                wrap="nowrap"
              >
                {years.map(y => (
                  <Typography
                    className={classNames(classes.text, {
                      [classes.activeText]: y === year,
                    })}
                    key={y}
                    onClick={() => onYearChange(y)}
                  >
                    {y}
                  </Typography>
                ))}
              </Grid>
            </PuiPopper>
          </div>
        </>
      )}
    </Grid>
  )
}

export default CalendarToolbarComponent
